import * as React from "react";
import ReactTooltip from "react-tooltip";
import { IQuickJournalStats } from "../../Data/QuickJournalTypes";
import {
    BuildQuickJournalHeatmapCells,
    GetQuickJournalHeatmapCellColor,
    GetQuickJournalHeatmapContainerStyle,
    quickJournalHeatmapFlashDurationInMilliseconds,
    GetQuickJournalHeatmapGridStyle,
    quickJournalHeatmapTooltipId,
} from "./QuickJournalHeatmapHelpers";
import { GetQuickJournalTheme, type QuickJournalTheme } from "../QuickJournalTheme";

export function QuickJournalStreakHeatmap({
    stats,
    flashCellKey,
    flashStartedAt,
    theme,
}: {
    stats: IQuickJournalStats;
    flashCellKey?: string;
    flashStartedAt?: number;
    theme?: QuickJournalTheme;
}) {
    const quickJournalTheme = GetQuickJournalTheme(theme);
    const [flashNow, setFlashNow] = React.useState(() => Date.now());
    const [shouldFadeRowsIn, setShouldFadeRowsIn] = React.useState(false);
    const [hoveredCellKey, setHoveredCellKey] = React.useState<string | null>(
        null
    );

    React.useEffect(() => {
        if (!flashCellKey || flashStartedAt === undefined) {
            return;
        }

        let frameId = 0;
        let isCancelled = false;

        const tick = () => {
            if (isCancelled) {
                return;
            }

            setFlashNow(Date.now());

            if (
                Date.now() - flashStartedAt <
                quickJournalHeatmapFlashDurationInMilliseconds
            ) {
                frameId = window.requestAnimationFrame(tick);
            }
        };

        tick();

        return () => {
            isCancelled = true;
            window.cancelAnimationFrame(frameId);
        };
    }, [flashCellKey, flashStartedAt]);

    React.useEffect(() => {
        const frameId = window.requestAnimationFrame(() => {
            setShouldFadeRowsIn(true);
        });

        return () => window.cancelAnimationFrame(frameId);
    }, []);

    React.useEffect(() => {
        ReactTooltip.rebuild();
    }, [stats.weeklyHourlyCounts]);

    const heatmap = BuildQuickJournalHeatmapCells(stats.weeklyHourlyCounts);
    const renderedRows = [...heatmap.rows].reverse();

    if (!heatmap.hasAnyActivity) {
        return null;
    }

    return (
        <div style={GetQuickJournalHeatmapContainerStyle(quickJournalTheme)}>
            <div style={GetQuickJournalHeatmapGridStyle()}>
                {renderedRows.map((row, rowIndex) => {
                    const staggerIndex = renderedRows.length - 1 - rowIndex;
                    const rowOpacity = shouldFadeRowsIn
                        ? Math.min(1, 0.52 + rowIndex * 0.09)
                        : 0;

                    return (
                        <div
                            key={row.rowLabel}
                            style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(24, 1fr)",
                                gap: "2px",
                                alignItems: "center",
                                opacity: rowOpacity,
                                transform: shouldFadeRowsIn
                                    ? "translateY(0)"
                                    : "translateY(4px)",
                                transition:
                                    "opacity 260ms ease, transform 260ms ease",
                                transitionDelay: `${staggerIndex * 70}ms`,
                            }}
                        >
                            {row.cells.map((cell) => {
                                const flashProgress =
                                    flashCellKey === cell.cellKey &&
                                    flashStartedAt !== undefined
                                        ? Math.min(
                                              1,
                                              (flashNow - flashStartedAt) /
                                                  quickJournalHeatmapFlashDurationInMilliseconds
                                          )
                                        : 1;
                                const isFlashTarget =
                                    flashCellKey === cell.cellKey &&
                                    flashStartedAt !== undefined &&
                                    flashProgress < 1;

                                return (
                                    <div
                                        key={cell.cellKey}
                                        data-for={quickJournalHeatmapTooltipId}
                                        data-tip={cell.tooltip}
                                        onMouseEnter={() =>
                                            setHoveredCellKey(cell.cellKey)
                                        }
                                        onMouseLeave={() =>
                                            setHoveredCellKey((currentKey) =>
                                                currentKey === cell.cellKey
                                                    ? null
                                                    : currentKey
                                            )
                                        }
                                        style={{
                                            width: "100%",
                                            height: "8px",
                                            borderRadius: "2px",
                                            position: "relative",
                                            overflow: "hidden",
                                            pointerEvents: "auto",
                                            background: GetQuickJournalHeatmapCellColor(
                                                cell.level,
                                                quickJournalTheme
                                            ),
                                            boxShadow: isFlashTarget
                                                ? quickJournalTheme.heatmap.flashBoxShadow
                                                : cell.level > 0
                                                    ? quickJournalTheme.heatmap.cellBoxShadow
                                                    : "none",
                                            outline:
                                                hoveredCellKey === cell.cellKey
                                                    ? quickJournalTheme.heatmap.hoverOutline
                                                    : "1px solid transparent",
                                            outlineOffset: "-1px",
                                            opacity: cell.level > 0
                                                ? quickJournalTheme.heatmap.activeOpacity
                                                : quickJournalTheme.heatmap.inactiveOpacity,
                                            transition:
                                                "box-shadow 120ms ease, opacity 120ms ease, outline-color 120ms ease",
                                        }}
                                    >
                                        {isFlashTarget && (
                                            <div
                                                style={{
                                                    position: "absolute",
                                                    inset: 0,
                                                    background:
                                                        "rgba(255, 255, 255, 1)",
                                                    opacity: 1 - flashProgress,
                                                    transition:
                                                        "opacity 40ms linear",
                                                }}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
            <ReactTooltip
                id={quickJournalHeatmapTooltipId}
                effect="solid"
                place="top"
            />
        </div>
    );
}
